<template>
  <div class="table-responsive">
    <b-table
      show-empty
      class="sticky-head-table table-wrapper"
      :fields="computedFields"
      :items="items"
      :foot-clone="isBusy && items.length > 0"
    >
      <template #empty>
        <div v-show="!isBusy" class="text-center my-4">Tidak ada data</div>
      </template>

      <template #cell(is_failed)="data">
        <div :class="`status ${!data.value ? 'status--success' : 'status--failed'}`">
          <i v-if="!data.value" class="mc-icon icon-fill_checkmark"></i>
          <i v-else class="mc-icon icon-outline_close"></i>
        </div>
      </template>

      <template #cell()="{ value, field, item }">
        <div class="d-flex flex-column detail-row">
          <div class="detail-row__value">
            {{ value || '-' }}
          </div>
          <div class="detail-row__error">
            {{ item[field.showErrorKey]?.error || '' }}
          </div>
        </div>
      </template>

      <template #head()="data">
        <div class="d-flex gap-3 justify-content-between" @click="handleSort(data)">
          <span class="label">
            {{ data.label }}
          </span>
          <div class="position-relative" v-if="data.field.isSortable">
            <i
              class="sort-icon-up mc-icon icon-outline_chevron-up"
              v-if="showSortingIcon(data.field.key, 'asc')"
            ></i>
            <i
              class="sort-icon-down mc-icon icon-outline_chevron-down"
              v-if="showSortingIcon(data.field.key, 'desc')"
            ></i>
          </div>
        </div>
      </template>

      <!-- For showing skeleton when fetching next page data, only rendered when items.length > 0, (see foot-clone attr) -->
      <template #foot()>
        <b-skeleton></b-skeleton>
      </template>
    </b-table>

    <!-- Observe visibility to get next page data -->
    <div
      v-observe-visibility="handleElementObserved"
      style="height: 2px"
      v-if="items.length > 0"
    ></div>
  </div>
</template>

<script>
export default {
  props: {
    fields: {
      type: Array,
      required: true,
    },
    listGetter: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      items: [],
      sorts: {},
      totalCount: 0,
      page: 1,
      limit: 10,
      isBusy: false,
    }
  },
  computed: {
    hasNextPage() {
      return this.page * this.limit < this.totalCount
    },
    computedFields() {
      return [
        {
          key: 'is_failed',
          label: '',
          thAttr: {
            style: 'min-width:40px;width:40px;',
          },
        },
        ...this.fields,
      ]
    },
  },
  watch: {
    sorts: {
      deep: true,
      handler(val) {
        this.page = 1
        this.getData({ limit: this.limit, page: 1, sorts: this.formatSorts(val) }, true)
      },
    },
  },
  mounted() {
    this.getData({
      limit: this.limit,
      page: 1,
      sorts: this.formatSorts(this.sorts),
    })
  },
  methods: {
    handleSort(data) {
      let { key, isSortable } = data.field

      if (!isSortable) return

      if (!(key in this.sorts)) {
        this.$set(this.sorts, key, 'asc')
        return
      }

      if (this.sorts[key] === 'asc') {
        this.$set(this.sorts, key, 'desc')
        return
      }

      this.$delete(this.sorts, key)
    },
    showSortingIcon(key, sortDirection) {
      if (!(key in this.sorts)) {
        return true
      }

      if (this.sorts[key] === sortDirection) {
        return true
      }

      return false
    },
    async getData(opts, reset = false) {
      this.isBusy = true

      if (reset) {
        this.items = []
      }

      const res = await this.listGetter(opts)

      if (!res) return

      this.items = [...this.items, ...res.data.paginated_result]
      this.totalCount = res.metadata.total_count
      this.isBusy = false
    },
    handleElementObserved(isVisible) {
      if (!isVisible) return
      if (!this.hasNextPage) return

      this.getData({
        limit: this.limit,
        page: ++this.page,
        sorts: this.formatSorts(this.sorts),
      })
    },
    formatSorts(sorts) {
      return Object.entries(sorts).reduce((prev, [key, value]) => [...prev, `${key} ${value}`], [])
    },
  },
}
</script>

<style lang="scss" scoped>
:deep(table) {
  td {
    position: relative;
    vertical-align: middle;
  }

  .label {
    white-space: nowrap;
    color: black;
  }

  .sort-icon-up,
  .sort-icon-down {
    position: absolute;
  }

  .sort-icon-up {
    top: 1px;
  }

  .sort-icon-down {
    bottom: 1px;
  }

  .status {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    display: grid;
    place-items: center;

    &--success {
      color: $green;
      background-color: $green-100;
    }

    &--failed {
      color: $red;
      background-color: #fee8e5;
    }
  }

  .detail-row {
    &__error {
      font-size: 10px;
      color: $red-400;
    }
  }
}
</style>
